/**
 *
 * ManageBag
 *
 */
import * as React from 'react';
import {
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Paper,
  Stack,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
  IconButton,
  AutocompleteChangeDetails,
  AutocompleteChangeReason,
  Avatar,
} from '@mui/material';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import DeleteIcon from '@mui/icons-material/Delete';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  TouchSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useDispatch, useSelector } from 'react-redux';
import { useArsenalSlice } from 'app/pages/Arsenal/slice';
import { useNavigate } from 'react-router-dom';
import {
  selectAllBags,
  selectAllBalls,
  selectArsenal,
} from 'app/pages/Arsenal/slice/selectors';
import { APIBagData, APIBallData } from 'app/pages/Arsenal/slice/types';
import { BallTypeIcon } from '../BallTypeIcon';

interface Props {
  arsenal?: any;
}

interface SortableItemProps {
  id: string;
  ball: APIBallData;
  onRemove: (ballId: string) => void;
}

function SortableItem({ id, ball, onRemove }: SortableItemProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <ListItem
      ref={setNodeRef}
      style={style}
      secondaryAction={
        <IconButton
          edge="end"
          aria-label="delete"
          onClick={() => onRemove(ball.id)}
          size="small"
        >
          <DeleteIcon />
        </IconButton>
      }
      disablePadding
      sx={{
        backgroundColor: isDragging ? 'action.hover' : 'transparent',
        borderRadius: 1,
        mb: 0.5,
      }}
    >
      <div
        {...attributes}
        {...listeners}
        style={{ width: '100%', touchAction: 'none' }}
      >
        <ListItemButton sx={{ py: 1.5 }} disableRipple>
          <ListItemIcon sx={{ minWidth: 32 }}>
            <DragIndicatorIcon fontSize="small" />
          </ListItemIcon>
          <Stack direction="row" spacing={2} alignItems="center">
            <BallTypeIcon
              polished={ball.surface_mode === 'Polished'}
              asymetrical={ball.core_int_diff !== 0}
              size={28}
            />
            <ListItemText
              primary={ball.ball_name}
              primaryTypographyProps={{ variant: 'body2' }}
            />
          </Stack>
        </ListItemButton>
      </div>
    </ListItem>
  );
}

export function ManageBag(props: Props) {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { actions } = useArsenalSlice();
  const balls = useSelector(selectAllBalls);
  const bags: APIBagData[] = useSelector(selectAllBags);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [selectedBalls, setSelectedBalls] = React.useState<APIBallData[]>([]);
  const [autocompleteValue, setAutocompleteValue] =
    React.useState<APIBallData | null>(null);

  const [bagAutocompleteValue, setBagAutocompleteValue] =
    React.useState<Partial<APIBagData> | null>(null);

  React.useEffect(() => {
    if (bagAutocompleteValue && bagAutocompleteValue.name) {
      const existingBag = bags.find(b => b.name === bagAutocompleteValue.name);
      if (existingBag) {
        setBagAutocompleteValue(existingBag);
      }
    }
    console.log('Bags updated:', bags);
  }, [bagAutocompleteValue, bags]);
  const handleAddBall = (ball: APIBallData | null) => {
    if (ball && !selectedBalls.find(b => b.id === ball.id)) {
      setSelectedBalls([...selectedBalls, ball]);
    }
    setAutocompleteValue(null);
  };

  const handleSaveBag = () => {
    if (bagAutocompleteValue) {
      if (!bagAutocompleteValue.id) {
        dispatch(
          actions.createBag({
            name: bagAutocompleteValue.name || 'unknown Bag',
            ballIds: selectedBalls.map(b => b.id),
          }),
        );
      } else {
        dispatch(
          actions.updateBag({
            id: bagAutocompleteValue.id,
            ballIds: selectedBalls.map(b => b.id),
          }),
        );
      }
    } else {
      console.log('updating existing bag with :', selectedBalls);
      console.log('Bag:', (bagAutocompleteValue as any)?.id);
    }
  };

  const handleDeleteBag = () => {
    if (bagAutocompleteValue && bagAutocompleteValue.id) {
      dispatch(
        actions.deleteBag({
          id: bagAutocompleteValue.id,
          navigate,
        }),
      );
    }
  };

  const handleRemoveBall = (ballId: string) => {
    setSelectedBalls(selectedBalls.filter(b => b.id !== ballId));
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      const oldIndex = selectedBalls.findIndex(b => b.id === active.id);
      const newIndex = selectedBalls.findIndex(b => b.id === over.id);
      setSelectedBalls(arrayMove(selectedBalls, oldIndex, newIndex));
    }
  };

  const availableBalls =
    balls?.filter(ball => !selectedBalls.find(b => b.id === ball.id)) || [];

  if (!balls) return <></>;

  const onBagNameChange = (
    event: React.SyntheticEvent<Element, Event>,
    value: string | Partial<APIBagData>,
    reason: AutocompleteChangeReason,
    details?: AutocompleteChangeDetails<string> | undefined,
  ) => {
    if (typeof value === 'string') {
      setSelectedBalls([]);
      setBagAutocompleteValue({ name: value });
    } else {
      const bagBalls =
        value?.balls?.map(b => balls.find(ball => ball.id === b.id)) || [];
      setSelectedBalls(bagBalls as APIBallData[]);
      setBagAutocompleteValue(value);
    }
  };

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, maxWidth: '95vw' }}>
      <Typography variant="h5" sx={{ mb: 3 }}>
        Manage Your Bag
      </Typography>

      <Stack spacing={3}>
        {/* Autocomplete Section */}
        <Card sx={{ pt: 2 }}>
          <CardContent>
            <Autocomplete
              freeSolo
              blurOnSelect
              autoSelect
              size="small"
              // value={bags[0].id || null}
              id="combo-box-bag-name"
              value={bagAutocompleteValue || null}
              options={bags as any}
              getOptionLabel={option =>
                (option as unknown as APIBagData).name || (option as any)
              }
              // isOptionEqualToValue={(option, value) => option.id === value}
              renderInput={params => (
                <div ref={params.InputProps.ref}>
                  <TextField
                    {...params}
                    label="Bag Name"
                    InputProps={{
                      ...params.InputProps,
                      placeholder: 'Select existing or add new',
                    }}
                  />
                </div>
              )}
              onChange={(event, value, reason) =>
                onBagNameChange(event, value as any, reason)
              }
            />
          </CardContent>
          <CardContent>
            <Typography variant="subtitle2" sx={{ mb: 2 }}>
              Add Balls to Your Bag
            </Typography>
            <Autocomplete
              options={availableBalls}
              getOptionLabel={option => option.ball_name}
              value={autocompleteValue}
              onChange={(_, value) => handleAddBall(value)}
              renderInput={params => (
                <TextField
                  {...params}
                  placeholder="Search and select a ball"
                  size={isMobile ? 'small' : 'medium'}
                />
              )}
              renderOption={(props, option) => (
                <Stack
                  component="li"
                  direction="row"
                  spacing={2}
                  {...props}
                  key={option.id}
                >
                  <BallTypeIcon
                    polished={option.surface_mode === 'Polished'}
                    asymetrical={option.core_int_diff !== 0}
                    size={28}
                  />
                  <li>{option.ball_name}</li>
                </Stack>
              )}
              fullWidth
              clearOnBlur
              blurOnSelect
              noOptionsText="No available balls"
            />
          </CardContent>
        </Card>

        {/* Selected Balls List Section */}
        <Card>
          <CardContent>
            <Typography variant="subtitle2" sx={{ mb: 2 }}>
              Your Selected Balls ({selectedBalls.length})
            </Typography>

            {selectedBalls.length > 0 ? (
              <DndContext
                // sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={handleDragEnd}
              >
                <SortableContext
                  items={selectedBalls.map(b => b.id)}
                  strategy={verticalListSortingStrategy}
                >
                  <List sx={{ width: '100%' }}>
                    {selectedBalls.map(ball => (
                      <SortableItem
                        key={ball.id}
                        id={ball.id}
                        ball={ball}
                        onRemove={handleRemoveBall}
                      />
                    ))}
                  </List>
                </SortableContext>
              </DndContext>
            ) : (
              <Paper
                sx={{
                  p: 3,
                  textAlign: 'center',
                  backgroundColor: 'action.hover',
                }}
              >
                <Typography color="textSecondary" variant="body2">
                  No balls selected yet. Add balls from the list above to get
                  started.
                </Typography>
              </Paper>
            )}
          </CardContent>
          <CardContent>
            <Stack direction="row" spacing={2} alignItems="center">
              <Button
                variant="contained"
                color="primary"
                onClick={handleSaveBag}
                fullWidth
              >
                {bagAutocompleteValue && bagAutocompleteValue.id
                  ? 'Update Bag'
                  : 'Save New Bag'}
              </Button>
              {bagAutocompleteValue && bagAutocompleteValue.id && (
                <Button
                  variant="outlined"
                  color="secondary"
                  onClick={handleDeleteBag}
                  fullWidth
                >
                  Delete Bag
                </Button>
              )}
            </Stack>
          </CardContent>
        </Card>
      </Stack>
    </Box>
  );
}
